import { useState, useRef, useCallback, useEffect } from 'react';

const MAX_DURATION_MS = 180000; // 3 minutes
const WARNING_THRESHOLD_MS = 150000; // 2:30
const TIMER_INTERVAL_MS = 100;

export interface UseWebVideoRecorderResult {
  isRecording: boolean;
  isPreviewing: boolean;
  isSupported: boolean;
  duration: number;
  showWarning: boolean;
  error: string | null;
  startPreview: (videoElement: HTMLVideoElement) => Promise<void>;
  startRecording: () => void;
  stopRecording: () => Promise<Blob | null>;
  stopPreview: () => void;
  clearError: () => void;
}

export function useWebVideoRecorder(): UseWebVideoRecorderResult {
  const [isRecording, setIsRecording] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [duration, setDuration] = useState(0);
  const [showWarning, setShowWarning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);
  const resolveStopRef = useRef<((blob: Blob | null) => void) | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startPreview = useCallback(async (videoElement: HTMLVideoElement) => {
    if (!isSupported) {
      setError('Video recording is not supported in this browser');
      return;
    }

    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: true,
      });

      streamRef.current = stream;
      videoElement.srcObject = stream;
      videoElement.muted = true;
      await videoElement.play();
      setIsPreviewing(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      if (message.includes('Permission denied') || message.includes('NotAllowedError')) {
        setError('Camera access denied. Please allow camera permissions.');
      } else if (message.includes('NotFoundError')) {
        setError('No camera found. Please connect a camera.');
      } else {
        setError(`Failed to access camera: ${message}`);
      }
    }
  }, [isSupported]);

  const startRecording = useCallback(() => {
    if (!streamRef.current) {
      setError('Camera preview not started');
      return;
    }

    try {
      chunksRef.current = [];
      // Prefer MP4 (H.264) for better Gemini compatibility, fallback to webm
      let mimeType = 'video/webm';
      if (MediaRecorder.isTypeSupported('video/mp4;codecs=avc1')) {
        mimeType = 'video/mp4;codecs=avc1';
      } else if (MediaRecorder.isTypeSupported('video/mp4')) {
        mimeType = 'video/mp4';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
        mimeType = 'video/webm;codecs=vp9';
      } else if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8')) {
        mimeType = 'video/webm;codecs=vp8';
      }
      console.log('Recording with mimeType:', mimeType);

      const mediaRecorder = new MediaRecorder(streamRef.current, { mimeType });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      const recordingMimeType = mimeType.split(';')[0]; // Get base mime type without codecs
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recordingMimeType });
        console.log('Recording complete, blob type:', recordingMimeType, 'size:', blob.size);
        if (resolveStopRef.current) {
          resolveStopRef.current(blob);
          resolveStopRef.current = null;
        }
      };

      mediaRecorder.onerror = () => {
        setError('Recording failed. Please try again.');
        setIsRecording(false);
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start(1000);
      startTimeRef.current = Date.now();
      setIsRecording(true);
      setShowWarning(false);
      setDuration(0);

      timerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setDuration(Math.floor(elapsed / 1000));

        if (elapsed >= WARNING_THRESHOLD_MS) setShowWarning(true);

        if (elapsed >= MAX_DURATION_MS) {
          if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
          }
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }
          setIsRecording(false);
          setShowWarning(false);
        }
      }, TIMER_INTERVAL_MS);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to start recording: ${message}`);
    }
  }, []);

  const stopRecording = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (!mediaRecorderRef.current || mediaRecorderRef.current.state === 'inactive') {
        resolve(null);
        return;
      }

      resolveStopRef.current = resolve;
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setShowWarning(false);
    });
  }, []);

  const stopPreview = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    setIsPreviewing(false);
    setIsRecording(false);
    setDuration(0);
    setShowWarning(false);
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    isRecording,
    isPreviewing,
    isSupported,
    duration,
    showWarning,
    error,
    startPreview,
    startRecording,
    stopRecording,
    stopPreview,
    clearError,
  };
}

export function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export function getMaxDurationSeconds(): number {
  return MAX_DURATION_MS / 1000;
}


